
import React, { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/db';
import { useAuth } from '../context/AuthContext';
import { SiteConfig, CompanySettings } from '../types';
import { PageBuilder } from './landing/PageBuilder';
import { ThemeManager } from './landing/ThemeManager';
import { AuthScreen } from './AuthScreen'; 
import { X } from 'lucide-react';
import { LandingSkeleton } from './ui/Skeleton';
import { DEFAULT_COMPANY_SETTINGS } from '../services/db/cms';
import { SEOHead } from './SEOHead';

export const PublicLanding: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading } = useAuth();
  
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  // Klick på Logga in/Starta innan inloggningskollen svarat: vänta in svaret
  // och gör det sedan. Knappen visar "Logga in" direkt istället för en snurra
  // som byts mot texten — snurran syns bara för den som hunnit klicka.
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // OPTIMIZATION: Läs in sparad design direkt från LocalStorage för "Instant Load"
  const cachedConfig = useMemo(() => {
      try {
          const raw = localStorage.getItem('cn_config_landing_page');
          return raw ? JSON.parse(raw) : undefined;
      } catch (e) { return undefined; }
  }, []);
  
  const cachedCompany = useMemo(() => {
      try {
          const raw = localStorage.getItem('cn_company_settings');
          return raw ? JSON.parse(raw) : undefined;
      } catch (e) { return undefined; }
  }, []);

  // Fetch fresh data in background (SWR)
  const { data: fetchedConfig, isLoading } = useSWR<SiteConfig>(
      'site-config-landing', 
      () => db.getSiteConfig('landing_page'),
      {
          revalidateOnFocus: false,
          fallbackData: cachedConfig, // Använd cachen direkt
          keepPreviousData: true      // Behåll gammal data medan ny hämtas (inget blinkande)
      }
  );

  const { data: fetchedCompany } = useSWR<CompanySettings>(
      'company-settings',
      () => db.getCompanySettings(),
      { 
          fallbackData: cachedCompany || DEFAULT_COMPANY_SETTINGS,
          revalidateOnFocus: false
      }
  );

  const config = fetchedConfig; 
  const companySettings = fetchedCompany || cachedCompany || DEFAULT_COMPANY_SETTINGS;

  useEffect(() => {
    if (isAuthOpen) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isAuthOpen]);

  const handleAction = (action: string) => {
      if (isAuthLoading && (action === 'LOGIN' || action === 'REGISTER' || action === 'REGISTER_GROUP')) {
          setPendingAction(action);
          return;
      }
      if (action === 'LOGIN') {
          if (user) {
              navigate('/dashboard');
          } else {
              setAuthMode('login');
              setIsAuthOpen(true);
          }
      } else if (action === 'REGISTER' || action === 'REGISTER_GROUP') {
          if (user) {
              navigate('/dashboard');
          } else {
              setAuthMode('register');
              setIsAuthOpen(true);
          }
      } else if (action === 'SCROLL_FEATURES') {
          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
      }
  };

  useEffect(() => {
      if (isAuthLoading || !pendingAction) return;
      setPendingAction(null);
      handleAction(pendingAction);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, pendingAction]);

  // Visa BARA skeleton om vi saknar både cache och färsk data (dvs första besöket någonsin)
  if (!config) return <LandingSkeleton />;

  // SEO Helpers
  const seoTitle = config.sections.find(s => s.type === 'HERO')?.title?.replace(/<[^>]*>?/gm, '').substring(0, 60);
  const seoDesc = config.sections.find(s => s.type === 'HERO')?.text?.substring(0, 160);
  const seoImage = config.sections.find(s => s.type === 'HERO')?.imageUrl;

  return (
    <>
      <SEOHead 
        title={seoTitle ? `${seoTitle} | CoffeeNova` : undefined}
        description={seoDesc}
        imageUrl={seoImage}
      />
      
      <ThemeManager theme={config.theme} />
      
      <PageBuilder 
          config={config} 
          companySettings={companySettings}
          onAction={handleAction}
          isAuthenticated={!!user}
          isAuthPending={pendingAction !== null}
      />

      {isAuthOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm transition-opacity" onClick={() => setIsAuthOpen(false)}></div>
              
              <div className="relative w-full max-w-md animate-fade-in-up z-10">
                  <div className="absolute top-4 right-4 z-50">
                      <button 
                        onClick={() => setIsAuthOpen(false)} 
                        className="bg-white/80 hover:bg-white p-2 rounded-full text-stone-500 hover:text-stone-900 transition-colors shadow-sm backdrop-blur-md"
                      >
                          <X size={20} />
                      </button>
                  </div>
                  <AuthScreen 
                      initialMode={authMode} 
                      onLoginSuccess={(u) => {
                          setIsAuthOpen(false);
                          navigate('/dashboard');
                      }} 
                  />
              </div>
          </div>
      )}
    </>
  );
};
